<script setup lang="ts">
import { computed, ref } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import {
  patientServiceCategoriesApi,
  type PatientServiceCategory,
  type PatientServiceSubcategoryForm,
} from '@/api/patient-service-categories';

interface Props {
  patientServiceCategory?: PatientServiceCategory | null;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const queryClient = useQueryClient();

const isEditing = !!props.patientServiceCategory;
const title = computed(() => (isEditing ? 'Editar Categoría' : 'Nueva Categoría'));

const MAX_NAME_LENGTH = 100;
const MAX_PRIORITY = 99;

const validationSchema = yup.object({
  name: yup
    .string()
    .required('El nombre es requerido')
    .max(MAX_NAME_LENGTH, `El nombre no puede exceder ${MAX_NAME_LENGTH} caracteres`),
  priority: yup
    .number()
    .typeError('La prioridad debe ser un número')
    .required('La prioridad es requerida')
    .integer('La prioridad debe ser un número entero')
    .min(0, 'La prioridad mínima es 0')
    .max(MAX_PRIORITY, `La prioridad máxima es ${MAX_PRIORITY}`),
});

const { values, errors, defineField, handleSubmit, meta } = useForm<{
  name: string;
  priority: number;
  prioritizationEnabled: boolean;
}>({
  validationSchema,
  initialValues: {
    name: props.patientServiceCategory?.name || '',
    priority: props.patientServiceCategory?.priority ?? 0,
    prioritizationEnabled: props.patientServiceCategory?.prioritizationEnabled ?? false,
  },
});

const [name, nameAttrs] = defineField('name');
const [priority, priorityAttrs] = defineField('priority');
const [prioritizationEnabled] = defineField('prioritizationEnabled');

// Discarded rows stay out of the editor: history keeps referencing them, and
// re-adding the same name undiscards the original row on the backend.
const subcategories = ref<PatientServiceSubcategoryForm[]>(
  (props.patientServiceCategory?.patientServiceSubcategories || [])
    .filter((subcategory) => !subcategory.discarded)
    .map((subcategory) => ({ id: subcategory.id, name: subcategory.name })),
);
const removedSubcategories = ref<PatientServiceSubcategoryForm[]>([]);

function addSubcategory() {
  subcategories.value.push({ name: '' });
}

function removeSubcategory(index: number) {
  const [removed] = subcategories.value.splice(index, 1);
  if (removed.id) {
    removedSubcategories.value.push({ id: removed.id, name: removed.name, _destroy: true });
  }
}

function subcategoriesAttributes() {
  const edited = subcategories.value
    .filter((subcategory) => subcategory.name.trim() !== '')
    .map((subcategory) => ({ ...subcategory, name: subcategory.name.trim() }));

  return [...edited, ...removedSubcategories.value];
}

const createMutation = useMutation({
  mutationFn: () => patientServiceCategoriesApi.create({
    ...values,
    patientServiceSubcategoriesAttributes: subcategoriesAttributes(),
  }),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['patientServiceCategories'] });
    emit('success');
    emit('close');
  },
});

const updateMutation = useMutation({
  mutationFn: () => patientServiceCategoriesApi.update({
    ...values,
    id: props.patientServiceCategory?.id,
    patientServiceSubcategoriesAttributes: subcategoriesAttributes(),
  }),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['patientServiceCategories'] });
    emit('success');
    emit('close');
  },
});

const isLoading = computed(() => createMutation.isPending.value || updateMutation.isPending.value);

const errorMessage = computed(() => {
  const error = (createMutation.error.value || updateMutation.error.value) as
    { response?: { data?: { errors?: string } } } | null;
  if (!error) return null;

  return error.response?.data?.errors ||
    'Ha ocurrido un error. Intenta nuevamente. Recuerda que el nombre no puede repetirse.';
});

const onSubmit = handleSubmit(() => {
  if (isEditing && props.patientServiceCategory) {
    updateMutation.mutate();
  } else {
    createMutation.mutate();
  }
});

function handleClose() {
  emit('close');
}
</script>
<template>
  <v-card>
    <v-card-title>{{ title }}</v-card-title>
    <v-card-text>
      <form @submit.prevent="onSubmit">
        <v-text-field
          v-model="name"
          label="Nombre de la categoría"
          :error-messages="errors.name"
          :disabled="isLoading"
          variant="outlined"
          class="mb-4"
          v-bind="nameAttrs"
        />

        <v-text-field
          v-model.number="priority"
          label="Prioridad en rutas"
          type="number"
          min="0"
          :max="MAX_PRIORITY"
          :error-messages="errors.priority"
          :disabled="isLoading"
          variant="outlined"
          class="mb-2"
          hint="0 a 99 — mayor prioridad = se mantiene primero al planificar rutas"
          persistent-hint
          v-bind="priorityAttrs"
        />

        <v-switch
          v-model="prioritizationEnabled"
          label="Permitir visitas prioritarias"
          hint="Al planificar rutas, las visitas marcadas como prioritarias se mantienen antes
            que las demás de esta categoría"
          persistent-hint
          color="primary"
          :disabled="isLoading"
          class="mb-2"
        />

        <div class="d-flex align-center justify-space-between mt-4 mb-2">
          <span class="text-subtitle-2">Subcategorías</span>
          <v-btn
            size="small"
            variant="text"
            color="primary"
            :disabled="isLoading"
            @click="addSubcategory"
          >
            <v-icon
              start
              icon="mdi-plus"
            />
            Agregar subcategoría
          </v-btn>
        </div>

        <div
          v-for="(subcategory, index) in subcategories"
          :key="subcategory.id ?? `new-${index}`"
          class="d-flex align-center mb-2"
        >
          <v-text-field
            v-model="subcategory.name"
            label="Nombre de la subcategoría"
            :disabled="isLoading"
            variant="outlined"
            density="compact"
            hide-details
          />
          <v-btn
            icon="mdi-delete"
            size="small"
            variant="text"
            color="error"
            :disabled="isLoading"
            @click="removeSubcategory(index)"
          />
        </div>
        <p
          v-if="subcategories.length === 0"
          class="text-body-2 text-medium-emphasis"
        >
          Sin subcategorías — los pacientes solo tendrán la categoría.
        </p>
      </form>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="isLoading"
        @click="handleClose"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        :disabled="!meta.valid"
        :loading="isLoading"
        @click="onSubmit"
      >
        {{ isEditing ? 'Actualizar' : 'Crear' }}
      </v-btn>
    </v-card-actions>

    <v-alert
      v-if="errorMessage"
      type="error"
      variant="tonal"
      class="mt-4"
    >
      {{ errorMessage }}
    </v-alert>
  </v-card>
</template>
